import React from "react"
import { Link } from "react-router-dom"
import SEO from "../components/SEO.jsx"

export default function WebDesignAdelaide() {
  return (
    <div className="bg-brand-bg text-brand-text">
      <SEO
        title="Web Design Adelaide | Business Website Design | 2merative"
        description="Web design Adelaide services for businesses that need a clear, credible, high-performing website. Built to convert, support growth, and reflect your brand properly."
        path="/web-design-adelaide"
        image="/og-image.png"
      />

      <section className="bg-gradient-to-b from-[#eef5ff] via-brand-bg to-brand-surface pt-32 pb-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="grid items-center gap-8 lg:grid-cols-[1.3fr_0.85fr]">
            <div className="max-w-[720px]">
              <p className="mb-4 inline-block rounded-full border border-[rgba(29,95,208,0.15)] bg-[rgba(29,95,208,0.09)] px-4 py-2 text-[0.82rem] font-bold uppercase tracking-[0.08em] text-brand-primary">
                Web Design Adelaide
              </p>

              <h1 className="m-0 text-[clamp(2.8rem,6vw,5rem)] font-bold leading-[0.96] tracking-[-0.03em] text-brand-heading">
                Websites built to
                <span className="block text-brand-primary">
                  win more enquiries
                </span>
              </h1>

              <p className="mt-6 max-w-[650px] text-[1.12rem] leading-[1.85] text-brand-textSoft">
                A good website should make your business look credible, explain
                clearly what you do, and help the right people take action. If
                your current site feels dated, unclear, or underwhelming, it may
                be costing you trust before a conversation even begins.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-gradient-to-r from-brand-primary to-[#3498db] px-6 py-3 text-[0.98rem] font-bold text-white shadow-button transition hover:-translate-y-[1px]"
                >
                  Get a Free Website Review
                </Link>

                <Link
                  to="/website-landing"
                  className="inline-flex min-h-[52px] items-center justify-center rounded-full border border-[rgba(22,53,90,0.14)] bg-brand-surface px-6 py-3 text-[0.98rem] font-bold text-[#16355a] transition hover:-translate-y-[1px]"
                >
                  View Website Packages
                </Link>
              </div>

              <ul className="mt-6 flex flex-wrap gap-3">
                <li className="rounded-full border border-brand-border bg-white/80 px-4 py-3 text-[0.92rem] font-semibold text-[#2f465f]">
                  Modern React builds
                </li>
                <li className="rounded-full border border-brand-border bg-white/80 px-4 py-3 text-[0.92rem] font-semibold text-[#2f465f]">
                  Conversion-focused structure
                </li>
                <li className="rounded-full border border-brand-border bg-white/80 px-4 py-3 text-[0.92rem] font-semibold text-[#2f465f]">
                  Clear local business positioning
                </li>
              </ul>
            </div>

            <aside className="rounded-xl3 border border-brand-border bg-white/85 p-6 shadow-[0_25px_60px_rgba(17,36,58,0.08)] backdrop-blur-[10px]">
              <div>
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Built for
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Service-based businesses
                </strong>
              </div>

              <div className="mt-4 border-t border-brand-border pt-4">
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Focus
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Clarity, trust, enquiries
                </strong>
              </div>

              <div className="mt-4 border-t border-brand-border pt-4">
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Approach
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Lean, modern, no outdated code
                </strong>
              </div>

              <div className="mt-4 border-t border-brand-border pt-4">
                <span className="mb-2 block text-[0.82rem] font-bold uppercase tracking-[0.12em] text-[#60748a]">
                  Best result
                </span>
                <strong className="block text-[1.15rem] leading-[1.4] text-[#0c1f34]">
                  Better flow from visit to contact
                </strong>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-brand-surface py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="mb-12 max-w-[760px]">
            <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
              The problem
            </p>
            <h2 className="mb-4 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
              Many businesses do not have a traffic problem. They have a trust
              and clarity problem.
            </h2>
            <p className="text-[1.05rem] leading-[1.75] text-brand-textSoft">
              A website can be live for years and still underperform if it does
              not create confidence quickly. The layout, language, structure,
              and calls to action all shape whether someone stays, trusts you,
              and gets in touch.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <article className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-3 text-xl font-semibold text-brand-heading">
                Clear first impression
              </h3>
              <p className="leading-[1.75] text-brand-textSoft">
                Your visitors should understand who you are, what you offer, and
                why they should trust you within seconds.
              </p>
            </article>

            <article className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-3 text-xl font-semibold text-brand-heading">
                Better customer flow
              </h3>
              <p className="leading-[1.75] text-brand-textSoft">
                Good web design guides people naturally from interest to
                enquiry, without confusion, clutter, or dead ends.
              </p>
            </article>

            <article className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-3 text-xl font-semibold text-brand-heading">
                Stronger business presence
              </h3>
              <p className="leading-[1.75] text-brand-textSoft">
                A polished website helps your business feel established,
                current, and professional across desktop and mobile.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-[#eef5ff] to-brand-surface py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="max-w-[760px]">
            <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
              What businesses usually need
            </p>
            <h2 className="mb-6 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
              Most businesses in Adelaide do not need a complicated website.
              They need a clear one.
            </h2>

            <div className="space-y-5 text-brand-textSoft">
              <p className="leading-[1.75]">
                Most businesses do not need a complicated website. They need a
                clear one. One that looks professional, loads properly on
                mobile, gives customers confidence, and makes it easy to get in
                touch.
              </p>
              <p className="leading-[1.75]">
                In many cases, the problem is not that a business has no
                website. It is that the existing one no longer reflects the
                quality of the business behind it. The layout feels old. The
                messaging is unclear. The structure does not guide people
                anywhere.
              </p>
              <p className="leading-[1.75]">
                That is where thoughtful web design makes a difference. The goal
                is not to add noise. It is to create a website that feels well
                considered, easy to use, and aligned with the business it
                represents.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="grid items-start gap-10 md:grid-cols-2">
            <div>
              <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
                What 2merative focuses on
              </p>
              <h2 className="mb-6 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
                Clear structure, strong messaging, and a better path to enquiry
              </h2>

              <div className="space-y-4 text-brand-textSoft">
                <p className="leading-[1.75]">
                  2merative builds websites with clarity first. That means
                  strong structure, clean visual hierarchy, practical content
                  flow, and a design that supports real business use.
                </p>
                <p className="leading-[1.75]">
                  The aim is not to overwhelm you with jargon or overbuilt
                  features. The aim is to create a website that feels right for
                  your business and helps you present properly online.
                </p>
                <p className="leading-[1.75]">
                  That might mean a full rebuild, a sharper service page
                  structure, stronger calls to action, or a better overall
                  customer journey. The right answer depends on where your
                  current website is helping and where it is getting in the way.
                </p>
                <p className="font-medium leading-[1.75] text-brand-heading">
                  We are based in Adelaide and love supporting local businesses.
                  If you are outside Adelaide, do not worry. We can still help
                  you too.
                </p>
              </div>
            </div>

            <div className="rounded-xl2 border border-brand-border bg-brand-surface p-8 shadow-card">
              <h3 className="mb-5 text-2xl font-semibold text-brand-heading">
                This may be a good fit if
              </h3>

              <ul className="space-y-3 leading-[1.75] text-brand-textSoft">
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Your website looks dated compared with the quality of your
                  business.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  You are getting traffic but not enough enquiries.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  Your site feels unclear, weak, or disconnected from your
                  brand.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  You want something more credible, modern, and easier to grow
                  with.
                </li>
                <li className="relative pl-6 before:absolute before:left-0 before:top-[0.1rem] before:font-extrabold before:text-brand-primary before:content-['✓']">
                  You want local Adelaide support and practical advice, not
                  technical noise.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-section">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="rounded-xl2 border border-brand-border bg-brand-surface p-8 text-center shadow-card">
            <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
              Packages
            </p>

            <h2 className="mb-4 text-[clamp(2rem,3vw,3rem)] leading-[1.1] text-brand-heading">
              Want to see packages and pricing?
            </h2>

            <p className="mx-auto mb-8 max-w-xl leading-[1.75] text-brand-textSoft">
              If you are ready to explore options, view our website packages and
              see what may suit your business best.
            </p>

            <Link
              to="/website-landing"
              className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-gradient-to-r from-brand-primary to-[#3498db] px-6 py-3 text-[0.98rem] font-bold text-white shadow-button transition hover:-translate-y-[1px]"
            >
              View Website Packages
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-b from-[#eef5ff] to-brand-surface py-section">
        <div className="mx-auto max-w-[1180px] px-6 text-center">
          <p className="mb-4 inline-flex items-center gap-2 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-brand-primary">
            Start with a practical review
          </p>

          <h2 className="mb-6 text-[clamp(2rem,3vw,3.3rem)] leading-[1.1] text-brand-heading">
            Not sure whether you need a full rebuild or just a stronger
            structure?
          </h2>

          <p className="mx-auto mb-8 max-w-3xl leading-[1.75] text-brand-textSoft">
            Start with a website health check. It is the fastest way to
            understand what is working, what is not, and what would make the
            biggest difference.
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-gradient-to-r from-brand-primary to-[#3498db] px-6 py-3 text-[0.98rem] font-bold text-white shadow-button transition hover:-translate-y-[1px]"
            >
              Book a Website Health Check
            </Link>

            
          </div>
        </div>
      </section>
    </div>
  )
}
